import {
  AloftArea,
  AloftAreaJSON,
  AloftPrice,
  CraftsmanSFRQualityClasses,
  HasFeatureAreaAndQuality,
  Improvements,
  ManualAdjustments,
  ORDER_FEATURES,
  Site,
  Transaction,
  applyRoundingAndThresholds,
  getFeatureQualityLabel,
  getFoundationDisplay,
  isDefined,
  isUndefined,
  manualAdjustmentKeys,
  orUndefined,
  schAloftPrice,
  syntaxFNMA,
  toPercentString,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { CompAdjustments } from './CompAdjustments.ts';
import { Adjustments, selectAdjustments } from './adjustmentSelectors/selectAdjustments.ts';
import { selectAdjustmentSettings, selectCompSequence } from './orderSelectors.ts';
import {
  selectImprovementsById,
  selectImprovementsEntities,
} from './propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from './propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSiteById, selectSiteEntities } from './propertyDataSelectors/siteSelectors.ts';
import {
  selectTransactionById,
  selectTransactionEntities,
} from './propertyDataSelectors/transactionSelectors.ts';

export const selectAdjustmentFeatures = createSelector(
  [selectImprovementsById, selectSiteById, selectTransactionById, (_state, id: string) => id],
  (improvements, site, transaction, id) =>
    getAdjustmentFeatures(site, improvements, transaction, id === 'subject'),
);

export const selectCompAdjustments = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectSiteEntities,
    selectTransactionEntities,
    selectManualAdjustmentsEntities,
    selectAdjustments,
    selectAdjustmentSettings,
  ],
  (
    compSequence,
    improvementsEntities,
    siteEntities,
    transactionEntities,
    manualAdjustmentsEntities,
    allAdjustments,
    adjustmentSettings,
  ) => {
    if (
      [
        compSequence,
        improvementsEntities,
        siteEntities,
        transactionEntities,
        manualAdjustmentsEntities,
        allAdjustments,
        adjustmentSettings,
      ].some((element) => isUndefined(element))
    ) {
      return {};
    }

    return Object.fromEntries(
      compSequence.map((compID) => {
        const improvements = improvementsEntities[compID];
        const site = siteEntities[compID];
        const transaction = transactionEntities[compID];
        const manualAdjustments = manualAdjustmentsEntities[compID];
        const compAdjustment = allAdjustments[compID];

        const features = getAdjustmentFeatures(site, improvements, transaction, false);

        const price = orUndefined(AloftPrice.schema.parse)(transaction?.price);
        const adjustments = getAdjustments(
          adjustmentSettings,
          compAdjustment,
          manualAdjustments,
          price,
        );

        const featuresAndAdjustments: CompAdjustments = {
          dateOfSale: {
            value: features?.dateOfSale.value,
            adjustment: adjustments.dateOfSale.adjustment,
          },
          termsOfSale: {
            value: features?.termsOfSale.value,
            adjustment: adjustments.termsOfSale.adjustment,
          },
          concessions: {
            value: features?.concessions.value,
            adjustment: adjustments.concessions.adjustment,
          },
          site: {
            size: {
              value: features?.site.size.value,
              adjustment: adjustments.site.size.adjustment,
            },
            location: {
              value: features?.site.location.value,
            },
            view: {
              value: features?.site.view.value,
            },
          },
          siteInfluence: { adjustment: adjustments.siteInfluence?.adjustment },
          siteView: { adjustment: adjustments.siteView?.adjustment },
          quality: {
            value: features?.quality.value,
            adjustment: adjustments.quality.adjustment,
          },
          condition: {
            value: features?.condition.value,
            adjustment: adjustments.condition.adjustment,
          },
          mainDwelling: {
            gla: {
              value: features?.mainDwelling.gla.value,
              adjustment: adjustments.mainDwelling.gla.adjustment,
            },
            units: {
              value: features?.mainDwelling.units.value,
              adjustment: adjustments.numberOfUnits?.adjustment,
            },
          },
          ...(improvements?.propertyType === 'multiFamily'
            ? {
                numberOfUnits: {
                  value: features?.numberOfUnits?.value,
                  adjustment: adjustments.numberOfUnits?.adjustment,
                },
              }
            : {}),
          basement: {
            value: features?.basement.value,
            adjustment: adjustments.basement.adjustment,
          },
          bathAboveGrade: {
            value: features?.bathAboveGrade.value,
            adjustment: adjustments.bathAboveGrade.adjustment,
          },
          bathBelowGrade: {
            value: features?.bathBelowGrade.value,
            adjustment: adjustments.bathBelowGrade.adjustment,
          },
          fullBaths: {
            adjustment: adjustments.fullBaths.adjustment,
          },
          halfBaths: {
            adjustment: adjustments.halfBaths.adjustment,
          },
          bedrooms: {
            adjustment: adjustments.bedrooms.adjustment,
          },
          actualAge: {
            adjustment: adjustments.actualAge?.adjustment,
          },
          garageCarport: {
            value: features?.garageCarport.value,
            adjustment: adjustments.garageCarport.adjustment,
          },
          porchPatioDeck: {
            value: features?.porchPatioDeck.value,
            adjustment: adjustments.porchPatioDeck.adjustment,
          },
          functionalUtility: adjustments.functionalUtility,
          externalObsolescence: adjustments.externalObsolescence,
          adu: {
            value: features?.adu.value,
            adjustment: adjustments.adu.adjustment,
          },
          aduHalfBaths: {
            value: features?.aduHalfBaths.value,
            adjustment: adjustments.aduHalfBaths.adjustment,
          },
          aduFullBaths: {
            value: features?.aduFullBaths.value,
            adjustment: adjustments.aduFullBaths.adjustment,
          },
          basementHalfBaths: {
            value: features?.basementHalfBaths.value,
            adjustment: adjustments.basementHalfBaths.adjustment,
          },
          basementFullBaths: {
            value: features?.basementFullBaths.value,
            adjustment: adjustments.basementFullBaths.adjustment,
          },
          basementBedrooms: {
            value: features?.basementBedrooms.value,
            adjustment: adjustments.basementBedrooms.adjustment,
          },
          basementFinished: {
            value: features?.basementFinished.value,
            adjustment: adjustments.basementFinished.adjustment,
          },
          basementTotal: {
            value: features?.basementTotal.value,
            adjustment: adjustments.basementTotal.adjustment,
          },
          barn: {
            value: features?.barn.value,
            adjustment: adjustments.barn.adjustment,
          },
          shed: {
            value: features?.shed.value,
            adjustment: adjustments.shed.adjustment,
          },
          workshop: {
            value: features?.workshop.value,
            adjustment: adjustments.workshop.adjustment,
          },
          pool: {
            value: features?.pool.value,
            adjustment: adjustments.pool.adjustment,
          },
          attic: {
            value: features?.attic.value,
            adjustment: adjustments.attic.adjustment,
          },
          fireplace: {
            value: features?.fireplace.value,
            adjustment: adjustments.fireplace.adjustment,
          },
          solarPanel: {
            value: features?.solarPanel.value,
            adjustment: adjustments.solarPanel.adjustment,
          },
          manual: adjustments.manual,
          totalCostToCure: adjustments.totalCostToCure,
          other: adjustments.other,

          netAdjustment: adjustments.netAdjustment,
          netAdjustmentPct: adjustments.netAdjustmentPct,
          grossAdjustmentPct: adjustments.grossAdjustmentPct,
          adjustedSalePrice: adjustments.adjustedSalePrice,
          depreciatedCost:
            adjustments.netAdjustment.adjustment && adjustments.dateOfSale?.adjustment
              ? {
                  adjustment: adjustments.netAdjustment.adjustment.sub(
                    adjustments.dateOfSale.adjustment,
                  ),
                }
              : undefined,
        };

        return [compID, featuresAndAdjustments] as [string, CompAdjustments];
      }),
    );
  },
);

const getAdjustmentFeatures = (
  site?: Site,
  improvements?: Improvements,
  transaction?: Transaction,
  isSubject = false,
) => {
  if (!site || !improvements || !transaction) return undefined;

  const { area, location, view, influence } = site;
  const {
    gla,
    uadQuality,
    craftsmanQuality,
    uadCondition,
    basement,
    fullBaths,
    halfBaths,
    garage,
    carport,
    detachedGarage,
    barn,
    shed,
    workshop,
    pool,
    spa,
    attic,
    fireplace,
    adu,
    effectiveAge,
    propertyType,
    solarPanel,
  } = improvements;
  const { concessions, termsOfSale } = transaction;
  const dateOfSale = isSubject ? undefined : formatDateOfSale(transaction);

  const foundationLabel =
    propertyType === 'manufacturedHome' ? getFoundationDisplay(improvements.foundation) : undefined;

  const numberOfUnits = propertyType === 'multiFamily' ? improvements.numberOfUnits : undefined;

  return {
    dateOfSale: toValue(dateOfSale),
    termsOfSale: toValue(termsOfSale),
    concessions: toValue(concessions),

    site: {
      size: toValue(orUndefined(AloftArea.schema.parse)(area)?.getSiteAreaString('')),
      location: toValue(location),
      view: toValue(view),
      influence: toValue(influence),
    },
    quality: toValue(`${getQuality(uadQuality, craftsmanQuality)}${foundationLabel ?? ''}`),
    condition: toValue(getCondition(uadCondition, effectiveAge)),
    mainDwelling: {
      gla: toValue(orUndefined(AloftArea.schema.parse)(gla)?.getSiteAreaString('')),
      units: toValue(numberOfUnits?.toString()),
    },
    numberOfUnits: toValue(numberOfUnits?.toString()),
    basement: toValue(getBasementFeature(basement)),
    bathAboveGrade: toValue(`${fullBaths ?? 0}.${halfBaths ?? 0}`),
    bathBelowGrade: toValue(`${basement?.fullBaths ?? 0}.${basement?.halfBaths ?? 0}`),
    garageCarport: toValue(getGarageCarportFeature(garage, carport, detachedGarage)),
    porchPatioDeck: toValue(getPorchPatioDeckFeature(improvements)),
    barn: toValue(getBarnShedWorkshopFeature('barn', barn)),
    shed: toValue(getBarnShedWorkshopFeature('shed', shed)),
    workshop: toValue(getBarnShedWorkshopFeature('workshop', workshop)),
    pool: toValue(getPoolSpaFeature(pool, spa)),
    attic: toValue(getAtticFeature(attic)),
    fireplace: toValue(getFireplaceFeature(fireplace)),
    adu: toValue(getADUFeature(adu)),
    aduHalfBaths: toValue(adu ? `${adu.halfBaths ?? 0}` : undefined),
    aduFullBaths: toValue(adu ? `${adu.fullBaths ?? 0}` : undefined),
    basementHalfBaths: toValue(basement ? `${basement.halfBaths ?? 0}` : undefined),
    basementFullBaths: toValue(basement ? `${basement.fullBaths ?? 0}` : undefined),
    basementBedrooms: toValue(basement ? `${basement.bedrooms ?? 0}` : undefined),
    basementFinished: toValue(
      orUndefined(AloftArea.schema.parse)(basement?.finishedArea)?.getSiteAreaString(''),
    ),
    basementTotal: toValue(
      orUndefined(AloftArea.schema.parse)(basement?.totalArea)?.getSiteAreaString(''),
    ),
    solarPanel: toValue(getSolarPanelFeature(solarPanel)),
  };
};

const formatDateOfSale = ({ status, contractDate, saleDate }: Transaction): string | undefined => {
  if (!status) return undefined;

  return syntaxFNMA.dateOfSaleSyntax.encode({
    listingStatus: status,
    contractMonth: contractDate,
    closeMonth: saleDate,
  });
};

const mergeManualAdjustments = (
  adjustments: Adjustments,
  manualAdjustments?: ManualAdjustments,
): Adjustments => {
  if (!manualAdjustments) return adjustments;

  manualAdjustmentKeys.forEach((key) => {
    const manualAdjustmentOverride = manualAdjustments[key];
    if (manualAdjustmentOverride) {
      switch (key) {
        case 'manual':
          // eslint-disable-next-line no-param-reassign
          adjustments.manualCost = schAloftPrice.parse(manualAdjustments.manual);
          break;
        case 'units':
          // eslint-disable-next-line no-param-reassign
          adjustments.numberOfUnits = schAloftPrice.parse(manualAdjustmentOverride);
          break;
        // This is for backwards compatibility where old orders had a single `baths` field
        case 'halfBaths':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.halfBaths = schAloftPrice.parse(manualAdjustmentOverride);
            const fullBathsAdjustment = manualAdjustments.fullBaths;
            // eslint-disable-next-line no-param-reassign
            adjustments.bathAboveGrade =
              AloftPrice.addWithNulls(
                fullBathsAdjustment ? schAloftPrice.parse(fullBathsAdjustment) : null,
                adjustments.halfBaths,
              ) ?? undefined;
          }
          break;
        // This is for backwards compatibility where old orders had a single `baths` field
        case 'fullBaths':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.fullBaths = schAloftPrice.parse(manualAdjustmentOverride);
            const halfBathsAdjustment = manualAdjustments.halfBaths;
            // eslint-disable-next-line no-param-reassign
            adjustments.bathAboveGrade =
              AloftPrice.addWithNulls(
                adjustments.fullBaths,
                halfBathsAdjustment ? schAloftPrice.parse(halfBathsAdjustment) : null,
              ) ?? undefined;
          }
          break;
        case 'basementFinished':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.basementFinished = schAloftPrice.parse(manualAdjustmentOverride);
            const basementTotalAdjustment = manualAdjustments.basementTotal;
            // eslint-disable-next-line no-param-reassign
            adjustments.basement =
              AloftPrice.addWithNulls(
                basementTotalAdjustment ? schAloftPrice.parse(basementTotalAdjustment) : null,
                adjustments.basementFinished,
              ) ?? undefined;
          }
          break;
        case 'basementTotal':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.basementTotal = schAloftPrice.parse(manualAdjustmentOverride);
            const basementFinishedAdjustment = manualAdjustments.basementFinished;
            // eslint-disable-next-line no-param-reassign
            adjustments.basement =
              AloftPrice.addWithNulls(
                adjustments.basementTotal,
                basementFinishedAdjustment ? schAloftPrice.parse(basementFinishedAdjustment) : null,
              ) ?? undefined;
          }
          break;
        case 'basementHalfBaths':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.basementHalfBaths = schAloftPrice.parse(manualAdjustmentOverride);
            // eslint-disable-next-line no-param-reassign
            adjustments.bathBelowGrade =
              AloftPrice.addWithNulls(
                adjustments.basementFullBaths ?? null,
                adjustments.basementHalfBaths,
              ) ?? undefined;
          }
          break;
        case 'basementFullBaths':
          if (manualAdjustmentOverride) {
            // eslint-disable-next-line no-param-reassign
            adjustments.basementFullBaths = schAloftPrice.parse(manualAdjustmentOverride);
            // eslint-disable-next-line no-param-reassign
            adjustments.bathBelowGrade =
              AloftPrice.addWithNulls(
                adjustments.basementFullBaths,
                adjustments.basementHalfBaths ?? null,
              ) ?? undefined;
          }
          break;
        default:
          // eslint-disable-next-line no-param-reassign
          adjustments[key] = schAloftPrice.parse(manualAdjustmentOverride);
          break;
      }
    }
  });

  return adjustments;
};

const getAdjustments = (
  adjustmentSettings: ReturnType<typeof selectAdjustmentSettings>,
  compAdjustments?: Adjustments,
  manualAdjustments?: ManualAdjustments,
  price?: AloftPrice,
) => {
  const {
    gla,
    numberOfUnits,
    condition,
    quality,
    site,
    siteInfluence,
    siteView,
    basementFullBaths,
    basementHalfBaths,
    basementBedrooms,
    aduFullBaths,
    aduHalfBaths,
    bathAboveGrade,
    bathBelowGrade,
    bedrooms,
    actualAge,
    garageCarport,
    porchPatioDeck,
    basement,
    poolSpa,
    shed,
    workshop,
    barn,
    attic,
    fireplace,
    manualCost,
    adu,
    functionalUtility,
    externalObsolescence,
    dateOfSale,
    totalCostToCure,
    other,
    termsOfSale,
    concessions,
    halfBaths,
    fullBaths,
    basementTotal,
    basementFinished,
    solarPanel,
  } = compAdjustments ?? {};

  const rowAdjustments: Adjustments = mergeManualAdjustments(
    {
      gla: applyRoundingAndThresholds(gla, 'mainDwelling', adjustmentSettings),
      numberOfUnits: applyRoundingAndThresholds(numberOfUnits, 'units', adjustmentSettings),
      condition: applyRoundingAndThresholds(condition, 'condition', adjustmentSettings),
      quality: applyRoundingAndThresholds(quality, 'quality', adjustmentSettings),
      site: applyRoundingAndThresholds(site, 'site', adjustmentSettings),
      siteView: applyRoundingAndThresholds(siteView, 'siteView', adjustmentSettings),
      siteInfluence: applyRoundingAndThresholds(siteInfluence, 'siteInfluence', adjustmentSettings),
      basementFullBaths: applyRoundingAndThresholds(
        basementFullBaths,
        'basementFullBaths',
        adjustmentSettings,
      ),
      basementHalfBaths: applyRoundingAndThresholds(
        basementHalfBaths,
        'basementHalfBaths',
        adjustmentSettings,
      ),
      basementBedrooms: applyRoundingAndThresholds(
        basementBedrooms,
        'basementBedrooms',
        adjustmentSettings,
      ),
      aduFullBaths: applyRoundingAndThresholds(aduFullBaths, 'aduFullBaths', adjustmentSettings),
      aduHalfBaths: applyRoundingAndThresholds(aduHalfBaths, 'aduHalfBaths', adjustmentSettings),
      shed: applyRoundingAndThresholds(shed, 'shed', adjustmentSettings),
      workshop: applyRoundingAndThresholds(workshop, 'workshop', adjustmentSettings),
      barn: applyRoundingAndThresholds(barn, 'barn', adjustmentSettings),
      bathAboveGrade: applyRoundingAndThresholds(
        bathAboveGrade,
        'bathAboveGrade',
        adjustmentSettings,
      ),
      bathBelowGrade: applyRoundingAndThresholds(
        bathBelowGrade,
        'bathBelowGrade',
        adjustmentSettings,
      ),
      halfBaths: applyRoundingAndThresholds(halfBaths, 'halfBaths', adjustmentSettings),
      fullBaths: applyRoundingAndThresholds(fullBaths, 'fullBaths', adjustmentSettings),
      basement: applyRoundingAndThresholds(basement, 'basement', adjustmentSettings),
      basementFinished: applyRoundingAndThresholds(
        basementFinished,
        'basementFinished',
        adjustmentSettings,
      ),
      basementTotal: applyRoundingAndThresholds(basementTotal, 'basementTotal', adjustmentSettings),
      garageCarport: applyRoundingAndThresholds(garageCarport, 'garageCarport', adjustmentSettings),
      porchPatioDeck: applyRoundingAndThresholds(
        porchPatioDeck,
        'porchPatioDeck',
        adjustmentSettings,
      ),
      poolSpa: applyRoundingAndThresholds(poolSpa, 'pool', adjustmentSettings),
      attic: applyRoundingAndThresholds(attic, 'attic', adjustmentSettings),
      fireplace: applyRoundingAndThresholds(fireplace, 'fireplace', adjustmentSettings),
      manualCost: applyRoundingAndThresholds(manualCost, 'manual', adjustmentSettings),
      adu: applyRoundingAndThresholds(adu, 'adu', adjustmentSettings),
      functionalUtility: applyRoundingAndThresholds(
        functionalUtility,
        'functionalUtility',
        adjustmentSettings,
      ),
      externalObsolescence: applyRoundingAndThresholds(
        externalObsolescence,
        'externalObsolescence',
        adjustmentSettings,
      ),
      dateOfSale: applyRoundingAndThresholds(dateOfSale, 'dateOfSale', adjustmentSettings),
      bedrooms,
      actualAge,
      totalCostToCure,
      other,
      termsOfSale,
      concessions,
      solarPanel,
    },
    manualAdjustments,
  );

  const adjustmentRowsToSum = Object.keys(rowAdjustments)
    .filter(
      (key) =>
        // These keys are already accounted in the bathroom, and basement adjustments so they shouldn't be double counted in the net and gross adjustment calculations
        ![
          'halfBaths',
          'fullBaths',
          'basementTotal',
          'basementFinished',
          'basementFullBaths',
          'basementHalfBaths',
        ].includes(key),
    )
    .map((key) => rowAdjustments[key as keyof Adjustments])
    .filter(isDefined);

  const netAdjustment = adjustmentRowsToSum.reduce(
    (acc: AloftPrice, curr) => acc.add(curr),
    AloftPrice.zero,
  );

  const grossAdjustment = adjustmentRowsToSum.reduce(
    (acc: AloftPrice, curr) => acc.add(curr?.abs() ?? AloftPrice.zero),
    AloftPrice.zero,
  );

  const netAdjustmentPct = netAdjustment?.ratio(price);
  const grossAdjustmentPct = grossAdjustment?.ratio(price);
  const adjustedSalePrice = AloftPrice.add(price, netAdjustment);

  return {
    mainDwelling: {
      gla: toAdjustment(rowAdjustments.gla),
    },
    numberOfUnits: toAdjustment(rowAdjustments.numberOfUnits),
    bathAboveGrade: toAdjustment(rowAdjustments.bathAboveGrade),
    basement: toAdjustment(rowAdjustments.basement),
    bathBelowGrade: toAdjustment(rowAdjustments.bathBelowGrade),
    bedrooms: toAdjustment(rowAdjustments.bedrooms),
    actualAge: toAdjustment(rowAdjustments.actualAge),
    garageCarport: toAdjustment(rowAdjustments.garageCarport),
    halfBaths: toAdjustment(rowAdjustments.halfBaths),
    fullBaths: toAdjustment(rowAdjustments.fullBaths),

    condition: toAdjustment(rowAdjustments.condition),
    quality: toAdjustment(rowAdjustments.quality),
    site: {
      size: toAdjustment(rowAdjustments.site),
    },
    siteView: toAdjustment(rowAdjustments.siteView),
    siteInfluence: toAdjustment(rowAdjustments.siteInfluence),
    basementTotal: toAdjustment(rowAdjustments.basementTotal),
    basementFinished: toAdjustment(rowAdjustments.basementFinished),
    basementHalfBaths: toAdjustment(rowAdjustments.basementHalfBaths),
    basementFullBaths: toAdjustment(rowAdjustments.basementFullBaths),
    basementBedrooms: toAdjustment(rowAdjustments.basementBedrooms),
    aduHalfBaths: toAdjustment(rowAdjustments.aduHalfBaths),
    aduFullBaths: toAdjustment(rowAdjustments.aduFullBaths),

    porchPatioDeck: toAdjustment(rowAdjustments.porchPatioDeck),
    pool: toAdjustment(rowAdjustments.poolSpa),
    barn: toAdjustment(rowAdjustments.barn),
    shed: toAdjustment(rowAdjustments.shed),
    workshop: toAdjustment(rowAdjustments.workshop),
    attic: toAdjustment(rowAdjustments.attic),
    fireplace: toAdjustment(rowAdjustments.fireplace),
    manual: toAdjustment(rowAdjustments.manualCost),
    adu: toAdjustment(rowAdjustments.adu),
    functionalUtility: toAdjustment(rowAdjustments.functionalUtility),
    externalObsolescence: toAdjustment(rowAdjustments.externalObsolescence),
    solarPanel: toAdjustment(rowAdjustments.solarPanel),

    dateOfSale: toAdjustment(rowAdjustments.dateOfSale),
    termsOfSale: toAdjustment(rowAdjustments.termsOfSale),
    concessions: toAdjustment(rowAdjustments.concessions),

    totalCostToCure: toAdjustment(rowAdjustments.totalCostToCure),
    other: toAdjustment(rowAdjustments.other),

    netAdjustment: {
      ...toValue(netAdjustment?.toString(true)),
      ...toAdjustment(netAdjustment),
    },
    netAdjustmentPct: toValue(netAdjustmentPct ? toPercentString(netAdjustmentPct) : '-'),
    grossAdjustmentPct: toValue(grossAdjustmentPct ? toPercentString(grossAdjustmentPct) : '-'),
    adjustedSalePrice: {
      ...toValue(adjustedSalePrice.toString(true)),
      ...toAdjustment(adjustedSalePrice),
    },
  };
};

export const getQuality = (
  uadQuality?: number,
  craftsmanQuality?: CraftsmanSFRQualityClasses,
): string => {
  const uadQualityString = orUndefined((val?: number) => `Q${val}`)(uadQuality);
  const craftsmanQualityString = orUndefined((val?: number) => `${val}`)(craftsmanQuality);
  return [uadQualityString, craftsmanQualityString].join('/');
};

export const getCondition = (uadCondition?: number, effectiveAge?: number): string => {
  const uadConditionString = orUndefined((val?: number) => `C${val}`)(uadCondition);
  const effectiveAgeString = orUndefined((val?: number) => `E${val}`)(effectiveAge);
  return [uadConditionString, effectiveAgeString].join('/');
};

const getBasementFeature = (basement?: {
  finishedArea?: AloftAreaJSON;
  totalArea?: AloftAreaJSON;
}): string => {
  const finishedArea = orUndefined(AloftArea.schema.parse)(
    basement?.finishedArea,
  )?.getSiteAreaString('');
  const totalArea = orUndefined(AloftArea.schema.parse)(basement?.totalArea)?.getSiteAreaString('');

  return [totalArea, finishedArea].join('');
};

const getGarageCarportFeature = (
  garage?: { area?: AloftAreaJSON; hasFeature?: boolean },
  carport?: { area?: AloftAreaJSON; hasFeature?: boolean },
  detachedGarage?: { area?: AloftAreaJSON; hasFeature?: boolean },
) => {
  if (!garage && !carport && !detachedGarage) return '';

  const garageString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} AG`,
  )(garage?.hasFeature ? garage?.area : undefined);
  const carportString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} Carport`,
  )(carport?.hasFeature ? carport?.area : undefined);
  const detachedGarageString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} DG`,
  )(detachedGarage?.hasFeature ? detachedGarage?.area : undefined);

  return [garageString, carportString, detachedGarageString].filter(isDefined).join(';');
};

const getPorchPatioDeckFeature = (improvements: Improvements) => {
  const result: string[] = [];
  if (ORDER_FEATURES.porch.hasFeature(improvements)) result.push('Porch');
  if (ORDER_FEATURES.patio.hasFeature(improvements)) result.push('Patio');
  if (ORDER_FEATURES.deck.hasFeature(improvements)) result.push('Deck');

  return result.join(';');
};

const getBarnShedWorkshopFeature = (
  type: 'barn' | 'shed' | 'workshop',
  structure?: HasFeatureAreaAndQuality,
) => {
  if (!structure) return '';

  const area = orUndefined(AloftArea.schema.parse)(structure.area)?.getSiteAreaString('');
  const quality = getFeatureQualityLabel(type, structure.quality, true);

  return `${area}/${quality}`;
};

const getAtticFeature = (attic?: Improvements['attic']) =>
  orUndefined(AloftArea.schema.parse)(attic?.area)?.getSiteAreaString('') ?? '';

const getFireplaceFeature = (fireplace?: Improvements['fireplace']) =>
  fireplace ? `${fireplace.type} (${fireplace.count})` : '';

const getSolarPanelFeature = (solarPanel?: Improvements['solarPanel']) =>
  solarPanel?.ownership ?? '';

const getPoolSpaFeature = (pool?: Improvements['pool'], spa?: Improvements['spa']) => {
  if (!pool && !spa) return '';

  const poolString = ORDER_FEATURES.pool.hasFeature({ pool } as Improvements)
    ? `${AloftArea.schema.parse(pool!.area).getSiteAreaString('')} Pool/${getFeatureQualityLabel(
        'pool',
        pool!.quality,
        true,
      )}`
    : undefined;

  const spaString = ORDER_FEATURES.spa.hasFeature({ spa } as Improvements)
    ? `${AloftArea.schema.parse(spa!.area).getSiteAreaString('')} Spa/${getFeatureQualityLabel(
        'spa',
        spa!.quality,
        true,
      )}`
    : undefined;

  return [poolString, spaString].filter(isDefined).join('; ');
};

const getADUFeature = (adu?: Improvements['adu']) => {
  if (!adu) return '';

  const area = orUndefined(AloftArea.schema.parse)(adu.area)?.getSiteAreaString('');
  return `${area}/QL${adu.quality}`;
};

const toValue = (value?: string): { value: string } => ({ value: value ?? '' });
const toAdjustment = (adjustment?: AloftPrice): { adjustment?: AloftPrice } => ({ adjustment });
